import dynamic from "next/dynamic";

import { HeroSection } from "components/heroSection/HeroSection";
import { PodcastEpisodesSection } from "components/podcastEpisodesSection";
import ProSection from "components/proSection/proSection";
import FeedsSection from "components/feedsSection/feedsSection";
import type { ProSectionData } from "components/proSection/proSection";
import { ArticleShort } from "lib/types/article";
import type {
  HomepageDatoProps,
  HomepageProps,
  ProCardCMSData,
} from "lib/types/homepage";

import Events from "./events";
import { LatestSection } from "./latestSection/latestSection";
import { Newsletters } from "./newsletters/newsletterSection";
import { SiftedInsightsSection } from "./siftedInsights/siftedInsightsSection";
import type { SiftedInsightsSectionData } from "./siftedInsights/siftedInsightsSection";
import { TalksSection } from "./talks/talksSection";

// Import TopBanner only on client-side to avoid hydration mismatch
// User data (window.Sifted.user) is only available in browser, not during SSR
const TopBanner = dynamic(
  () => import("components/topBanner/topBanner").then((mod) => mod.TopBanner),
  { ssr: false }
);

type HomepageWrapperProps = {
  data: HomepageDatoProps;
  homepage: HomepageProps;
};

const transformProSectionData = (data: {
  proSectionTitle?: string;
  proSectionCtaText?: string;
  proSectionCtaLink?: string;
  proCards?: ProCardCMSData[];
}): ProSectionData | null => {
  if (!data.proCards || data.proCards.length === 0) {
    return null;
  }

  const validCards = data.proCards.filter(
    (card) => card.ctaButtonText && card.ctaButtonLink
  );

  if (validCards.length === 0) {
    return null;
  }

  return {
    sectionTitle: data.proSectionTitle || "Sifted Pro",
    sectionButtonText: data.proSectionCtaText || "Find out more",
    sectionButtonUrl: data.proSectionCtaLink || "/sifted-pro",
    cards: validCards.map((card) => ({
      headline: card.headline,
      description: card.description,
      image: card.cardImage,
      buttonText: card.ctaButtonText!,
      buttonUrl: card.ctaButtonLink!,
    })),
  };
};

const getSiftedInsightsSectionData = (
  data: HomepageDatoProps
): SiftedInsightsSectionData | null => {
  if (!data.featureCards || data.featureCards.length === 0) {
    return null;
  }

  return {
    sectionTitle: data.siftedInsightsTitle || "Sifted Insights",
    featureCards: data.featureCards,
    featuredArticles: (data.insightsFeaturedArticles || []) as ArticleShort[],
  };
};

export const TopBannerWrapper = () => <TopBanner />;

export const HeroSectionWrapper = ({
  data,
  homepage,
}: HomepageWrapperProps) => (
  <HeroSection
    heroArticle={data.heroArticle as ArticleShort}
    featuredHeroArticles={data.featuredHeroArticles as ArticleShort[]}
    recentArticles={homepage.latest}
    callout={data.callout}
    excludedLatestArticlesCategories={data.latestArticlesCategoriesFilter?.map(
      (filter) => filter.text
    )}
  />
);

export const ProSectionWrapper = ({
  data,
}: Pick<HomepageWrapperProps, "data">) => {
  const proSectionData = transformProSectionData({
    proSectionTitle: data.proSectionTitle,
    proSectionCtaText: data.proSectionCtaText,
    proSectionCtaLink: data.proSectionCtaLink,
    proCards: data.proCards,
  });

  return <>{proSectionData && <ProSection data={proSectionData} />}</>;
};

export const FeedsAndPodcastSectionWrapper = ({
  data,
  homepage,
}: HomepageWrapperProps) => (
  <div className="mx-auto w-full max-w-content-mobile px-5 md:max-w-content lg:max-w-container lg:px-0 mdDesktop:max-w-container mdDesktop:px-5">
    <div className="flex flex-col gap-6 lg:flex-row mdDesktop:flex-row">
      <div className="h-full w-full lg:w-1/2 mdDesktop:w-1/2">
        {homepage.feedsSectionData && (
          <FeedsSection sectionData={homepage.feedsSectionData} />
        )}
      </div>

      <div className="w-full lg:w-1/2 mdDesktop:w-1/2">
        {(data.latestEpisodes || []).length > 0 ||
        (data.topEpisodes || []).length > 0 ? (
          <PodcastEpisodesSection
            latestEpisodes={data.latestEpisodes || []}
            topEpisodes={data.topEpisodes || []}
            podcastCallout={data.podcastCallout}
            sectionTitle={data.podcastSectionHeader?.title}
            seeAllText={data.podcastSectionHeader?.cta?.text}
            seeAllUrl={data.podcastSectionHeader?.cta?.url}
            calloutHref={data.podcastCallout?.buttonCta}
          />
        ) : null}
      </div>
    </div>
  </div>
);

export const NewslettersWrapper = () => <Newsletters />;

export const LatestSectionWrapper = ({
  data,
}: Pick<HomepageWrapperProps, "data">) => (
  <>
    {data.latestItems && data.latestItems.length > 0 && (
      <LatestSection
        headerTitle={data.latestHeader?.title}
        headerCtaText={data.latestHeader?.cta?.text}
        headerCtaUrl={data.latestHeader?.cta?.url}
        items={data.latestItems}
      />
    )}
  </>
);

export const SiftedInsightsSectionWrapper = ({
  data,
}: Pick<HomepageWrapperProps, "data">) => {
  const siftedInsightsSectionData = getSiftedInsightsSectionData(data);

  return (
    <>
      {siftedInsightsSectionData && (
        <SiftedInsightsSection data={siftedInsightsSectionData} />
      )}
    </>
  );
};

export const EventsSectionWrapper = () => (
  <div className="mx-auto w-full max-w-content-mobile px-5 md:max-w-content lg:max-w-container lg:px-0 mdDesktop:max-w-container mdDesktop:px-5">
    <Events />
  </div>
);

export const TalksSectionWrapper = () => (
  <div className="mx-auto w-full max-w-content-mobile px-5 md:max-w-content lg:max-w-container lg:px-0 mdDesktop:max-w-container mdDesktop:px-5">
    <TalksSection />
  </div>
);
